import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { catchError, delay, mergeMap, Observable, of, retry, retryWhen, take, throwError, timeout } from "rxjs";
import { environment } from "../../environments/environment";

export interface Settings {
	decimal_point: string,
	enable_volume_calculator: boolean,
	currency: string,

	// If you don't need the data immediately, this one is best
	destination_type: string,

	ecommerce_price_int_1: string,
	ecommerce_price_int_2: string,
	ecommerce_price_int_3: string,
	ecommerce_price_int_4: string,
	ecommerce_price_nat_1: string,
	ecommerce_price_nat_2: string,
	ecommerce_price_nat_3: string,
	ecommerce_price_nat_4: string,
	individual_ecommerce_prices: boolean,
	utm_ecommerce: boolean,
	privacy_policy_url: string,
	home_url: string,
	volume_type: string,
	utm_prices?: any
	has_dashboard: boolean,
	meta_title: string,
	meta_desc: string
}

export interface Country {

	country_code: string;
	country: string;
}
export type Countries = Country[];

export interface Mover {
	id: number;
	name: string;
	logo: string | null;
	rating: string;
	rating_amount: number;
	city: string;
	checked: number;
}
export type Movers = Mover[];

@Injectable( {
	providedIn: 'root'
} )
export class ApiService {

	public domainData: any;
	public language: string;

	constructor( private http: HttpClient,
	             @Inject(LOCALE_ID) private locale: string) {

		// Take only the language part from the locale
		this.language = this.locale.split('-')[0];
	}

	private getPath() {

		var path = window.location.protocol + "//" + window.location.host + '/wp-json/';
		var namespace = 'super-form/v1/';

		if( ! environment.production && ! environment.staging ) {

			// Local
			path = environment.restApiDomain + '/wp-json/';
		}

		path += namespace;

		return path;
	}

	private get( route: string ) {

		return this.http.get( this.getPath() + route )
			.pipe( retry( 2 ), catchError( this.handleError ) );
	}

	private getPublic( url: string ) {

		return this.http.get( url )
			.pipe( retry( 2 ), catchError( this.handleError ) );
	}

	private post( route: string, body: any, retryCount: number = 0 ) {

		console.log( 'Posting to: ' + this.getPath() + route );

		if( retryCount > 0 ) {

			return this.http.post<any>( this.getPath() + route, body )
				.pipe(
					retryWhen(errors => {
						let retryAttempts = retryCount - 1;
						return errors.pipe(
							mergeMap(error => {

								if( retryAttempts > 0 ) {

									// Log the error without throwing until the final retry attempt
									console.log( '%cError during post. We will throw an actual error with the final retry. (' + retryAttempts + ' attempts left)', 'color: red' );
									console.log( 'Error during request:', error );

									retryAttempts--;
									// Delay between retries, you can adjust this if needed
									return of(error).pipe(delay(1000));
								}
								else {
									// On the final retry attempt, throw the error
									return throwError( () => 'Maximum retry attempts reached' );
									throw 'error';
								}
							}),
							take(retryCount)
						);
					}),
					// Max allowed is 10 seconds
					timeout(10000 ),
					catchError( error => {

						console.error('Maximum retry attempts reached:', error);
						return throwError( () => 'Maximum retry attempts reached final' );
					} )
			);
		}
		else {

			return this.http.post<any>( this.getPath() + route, body )
				.pipe( retry( retryCount ), timeout(10000), catchError( this.handleError ) );
		}
	}

	private postPublic( url: string, body: any, headers?: any ): Observable<any> {

		return this.http.post( url, body, { headers } );
	}

	public getVcItems() {

		return this.get( 'vc/' + this.language );
	}

	public getVcPreItems( token: string ) {

		// It seems that we cache the API that this goes to. Added random parameter
		return this.get( 'vc-pre-items/' + token + '?nocache=' + (Math.random() + 1).toString(36).substring(7) );
	}

	/**
	 * Get complete list of countries from API in correct language
	 */
	public getCountries(): Observable<Countries> {

		return <Observable<Countries>>this.get( 'countries/' + this.language );
	}

	/**
	 * Get list of popular countries from API in correct language
	 */
	public getPopularCountries(): Observable<Countries> {

		return <Observable<Countries>>this.get( 'popular-countries/' + this.language );
	}

	public getAppSettings(): Observable<Settings> {

		// This observable makes use of the settings interface
		return <Observable<Settings>>this.get( 'settings' );
	}

	public postRequest( body: any ): Observable<any> {

		return this.post( 'send-request', body, 10 );
	}

	public postQuestions( body: any ): Observable<any> {

		return this.post( 'send-questions', body );
	}

	public postAskPrematch( body: any ): Observable<Movers> {

		return this.post( 'ask-prematch', body ).pipe(
			timeout(15000)
		);
	}

	public getPossibleMatches( body: any ): Observable<any> {

		return this.post( 'get-matches', body );
	}

	public getCustomer( customerKey: string ): Observable<any> {

		return this.get( 'get-customer?customer=' + customerKey );
	}

	public getTestQuestions() : Observable<any> {

		return this.get( 'test-questions' );
	}

	public getPostCodeData( postcode: string ) : Observable<any> {

		// This api is locked behind a domain setting
		var apiKey = 'pi868TFXyFya6BBr';

		return this.getPublic( 'https://api.pro6pp.nl/v1/autocomplete?auth_key=' + apiKey + '&nl_sixpp=' + postcode );
	}

	public getZeroBounceResult( email: string ): Observable<any> {

		// @TODO: This api key needs to be secured behind a domain setting
		var apiKey = environment.zeroBounceApiKey;
		var apiUrl = 'https://api.zerobounce.net/v2/validate';

		return this.getPublic( apiUrl + '?api_key=' + apiKey + '&email=' + email );
	}

	public getMistralAIResult( data: string ): Observable<any> {

		const endpoint = 'send-to-mistral';
		return this.post( endpoint, data );
	}

	private handleError( error: HttpErrorResponse ) {

		if( error.error instanceof ErrorEvent ) {

			// A client-side or network error occurred. Handle it accordingly.
			console.error( 'An error occurred:', error.error.message );

		} else {

			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong,
			console.error( `Backend returned code ${error.status}, ` + `body was: ${error.error}` );
			console.log( error.error );
		}

		console.log( 'Error in json format:', JSON.stringify( error ) );

		// return an observable with a user-facing error message
		return throwError( () => 'Something bad happened; please try again later.' );
	}
}
